import { grey } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

import type { PaletteMode } from '@mui/material';

import {
  GX_ADDITIONAL_PAPER,
  GX_CURVE_SELECTOR,
  GX_FAILURE,
  GX_FAILURE_DARK,
  GX_FAILURE_LIGHT,
  GX_FORWARD_HIGHLIGHT,
  GX_HIGHLIGHT,
  GX_HIGHLIGHT_LIGHT,
  GX_INFO_MAIN,
  GX_MAIN_BACKGROUND,
  GX_MINI_TABLE_HIGHLIGHT,
  GX_NEUTRAL,
  GX_RESPONSE,
  GX_SELECTION,
  GX_SPOT_HIGHLIGHT,
  GX_SUBTEXT,
  GX_SUCCESS,
  GX_TEXT,
  GX_TEXT_DISABLED,
  GX_TEXT_SECONDARY,
  GX_TILE_DARK,
  GX_TILE_LIGHT,
  GX_WARNING
} from './colors';

const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 2000
};

const getDesignTokens = (mode: PaletteMode) => ({
  breakpoints: {
    values: breakpoints
  },
  palette: {
    mode,
    gx: {
      text: GX_TEXT,
      textSecondary: GX_TEXT_SECONDARY,
      subtext: GX_SUBTEXT,
      textDisabled: GX_TEXT_DISABLED,
      neutral: GX_NEUTRAL,
      tileLight: GX_TILE_LIGHT,
      tileDark: GX_TILE_DARK,
      additionalPaper: GX_ADDITIONAL_PAPER,
      mainBackground: GX_MAIN_BACKGROUND,
      highlight: GX_HIGHLIGHT,
      highlightLight: GX_HIGHLIGHT_LIGHT,
      failure: GX_FAILURE,
      failureLight: GX_FAILURE_LIGHT,
      failureDark: GX_FAILURE_DARK,
      success: GX_SUCCESS,
      warning: GX_WARNING,
      infoMain: GX_INFO_MAIN,
      response: GX_RESPONSE,
      actionBarButtonTile: 'rgba(248, 248, 248, 0.20)',
      selectorForwardCurve: GX_CURVE_SELECTOR
    },
    warning: {
      main: GX_WARNING
    },
    error: {
      main: GX_FAILURE
    },
    success: {
      main: GX_SUCCESS
    },
    info: {
      main: GX_INFO_MAIN,
      dark: GX_TEXT_DISABLED,
      light: GX_NEUTRAL
    },
    primary: {
      ...(mode === 'dark' && {
        main: GX_HIGHLIGHT,
        light: GX_HIGHLIGHT_LIGHT
      })
    },
    secondary: {
      ...(mode === 'dark' && {
        main: GX_TILE_LIGHT
      })
    },
    ...(mode === 'dark' && {
      background: {
        default: GX_MAIN_BACKGROUND,
        paper: GX_TILE_DARK,
        additionalPaper: GX_ADDITIONAL_PAPER
      }
    }),
    text: {
      ...(mode === 'light'
        ? {
            primary: grey[900],
            secondary: GX_HIGHLIGHT,
            subtext: GX_SUBTEXT
          }
        : {
            primary: GX_TEXT,
            secondary: GX_TEXT_SECONDARY,
            subtext: GX_SUBTEXT
          })
    },
    table: {
      forwardCurve: GX_FORWARD_HIGHLIGHT,
      spot: GX_SPOT_HIGHLIGHT,
      infoRowBg: '#2E3340',
      infoRowText: '#cccccc',
      highlight: GX_SELECTION,
      miniTableHighlight: GX_MINI_TABLE_HIGHLIGHT
    }
  },
  typography: {
    fontFamily: 'Cairo, Arial, sans-serif',
    color: GX_TEXT_SECONDARY,
    h1: {
      fontSize: '1.75rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '2rem'
      },
      [`@media (min-width:${breakpoints.md}px)`]: {
        fontSize: '2.25rem'
      },
      fontWeight: 700
    },
    h2: {
      fontSize: '1.5rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '1.75rem'
      },
      [`@media (min-width:${breakpoints.md}px)`]: {
        fontSize: '2rem'
      },
      fontWeight: 700
    },
    h3: {
      fontSize: '1rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '1.25rem'
      },
      [`@media (min-width:${breakpoints.md}px)`]: {
        fontSize: '1.5rem'
      },
      fontWeight: 700
    },
    h4: {
      fontSize: '0.875rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '1rem'
      },
      [`@media (min-width:${breakpoints.md}px)`]: {
        fontSize: '1.25rem'
      },
      fontWeight: 700
    },
    h5: {
      fontSize: '0.875rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '1rem'
      },
      [`@media (min-width:${breakpoints.md}px)`]: {
        fontSize: '1rem'
      },
      fontWeight: 700
    },
    h6: {
      fontSize: '0.75rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '0.875rem'
      },
      [`@media (min-width:${breakpoints.md}px)`]: {
        fontSize: '0.875rem'
      },
      fontWeight: 700
    },
    subtitle1: {
      fontSize: '0.875rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '1rem'
      },
      [`@media (min-width:${breakpoints.md}px)`]: {
        fontSize: '1.25rem'
      },
      fontWeight: 500
    },
    subtitle2: {
      fontSize: '0.875rem',
      [`@media (min-width:${breakpoints.sm}px)`]: {
        fontSize: '1rem'
      },
      fontWeight: 500
    }
  },
  shape: {
    borderRadius: 0
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Cairo, Arial, sans-serif'
        }
      }
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave'
      },
      styleOverrides: {
        text: {
          borderRadius: '16px/22px',
          fontSize: '16px'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          lineHeight: '1.5',
          fontFamily: 'Cairo, Arial, sans-serif',
          textTransform: 'initial',
          variants: [
            {
              props: { variant: 'text', color: 'primary' },
              style: {
                color: GX_TEXT
              }
            },
            {
              props: { variant: 'text', color: 'secondary' },
              style: {
                color: GX_HIGHLIGHT
              }
            },

            {
              props: { variant: 'outlined' },
              style: {
                color: GX_TEXT,
                borderColor: GX_TEXT,
                '&:hover': {
                  backgroundColor: '#9993912b'
                }
              }
            }
          ],
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
            marginRight: '8px'
          }
        }
      }
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: 'Cairo, Arial, sans-serif',
          fontSize: '14px',
          px: 0.2,
          '&.Mui-disabled': {
            opacity: 0.5
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundImage: 'none!important' // Remove MUI's background-image globally
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '14px',
          backgroundImage: 'none!important', // Remove MUI's background-image globally
          fontFamily: 'Cairo, Arial, sans-serif',
          placeholder: {
            color: GX_TEXT_DISABLED
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          padding: '2px 0'
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottom: 'none'
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: 'none'
          },
          '&:after': {
            borderBottom: 'none'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: GX_TILE_LIGHT,
          textTransform: 'capitalize',
          color: GX_TEXT_SECONDARY,
          'div.MuiBox-root': {
            button: {
              color: GX_TEXT_SECONDARY
            }
          },
          '&.Mui-selected': {
            color: GX_TEXT,
            'div.MuiBox-root': {
              button: {
                color: GX_TEXT
              }
            }
          },
          '&:focus': {
            outline: 'none'
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          width: '14px',
          height: '14px',
          padding: 0
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          width: '14px',
          height: '14px',
          padding: '0px'
        },
        colorPrimary: {
          '& .MuiSvgIcon-root': {
            width: '18px',
            height: '18px'
          },
          '&.Mui-checked': {
            color: GX_TEXT
          }
        }
      },
      defaultProps: {
        size: 'small'
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
          gap: '8px'
        },
        label: {
          fontSize: '14px'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          position: 'relative',
          zIndex: 'auto',
          borderRight: `1px solid ${GX_TILE_LIGHT}`
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': {
            display: 'none'
          },
          '&.Mui-expanded': {
            marginTop: 0,
            marginBottom: 0
          },
          boxShadow: 'none'
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '36px',
          '&.Mui-expanded': {
            minHeight: '36px',
            marginTop: 0
          }
        },
        content: {
          margin: '8px 0',
          '&.Mui-expanded': {
            margin: '8px 0'
          }
        }
      }
    }
  }
});

// @ts-expect-error mui-config
export const darkModeTheme = createTheme(getDesignTokens('dark'));
